import { Api, ApiError } from "../api";
import { reload } from "../nav";
import { h, label, pill, replace } from "../dom";
import { when } from "../format";
import type { Me } from "../types";
import { can, confirmButton, crumbs, orderCrumbs } from "./shared";
import { plain } from "../words";

// One document read from the groups' mail: what the reader made of it and why it needs a person (the note carries the
// checks' reasons). With documents.write, settle it (settle_document): confirm the reading (optionally with the PO
// number it should have read), read it again, or ignore it with a reason.

interface Reading {
  document?: {
    documentId: number; filename: string; kind: string; status: string; note: string | null; model: string | null;
    confidence: number | null; readAt: string | null; party: string | null;
    customerPo: { customerPoId: number; poNumber: string } | null;
    /** The vendor PO a supplier's document (invoice, BOL) is recorded on. */
    vendorPo?: { vendorPoId: number; number: string } | null;
    extracted: {
      summary?: string; document_number?: string | null; issued_by?: string | null;
      uncertain?: { field: string; reason: string }[];
      purchase_order?: {
        po_number: string | null; po_date: string | null; buyer_company: string | null; seller_company: string | null;
        ship_to: string | null; requested_date: string | null; terms: string | null; total: string | null;
        lines: { line_type: string; line_no: number | null; customer_part: string | null; description: string | null;
                 qty: number | null; uom: string | null; unit_price: string | null; price_per: string | null; extended: string | null }[];
      } | null;
    } | null;
    from: string; subject: string; receivedAt: string; mailbox: string; knownSender?: boolean;
  };
  error?: string;
}

interface Settled {
  status: string;
  reason?: string;
  note?: string;
  action?: string;
  result?: { status?: string; note?: string; customerPoId?: number } | null;
}

/** What settling did, in a line, with a link to the order a confirmed document became. */
/** Where a confirmed document now sits, from its reading: the vendor PO it's recorded on, or the order it became. */
type Placed = Pick<NonNullable<Reading["document"]>, "vendorPo" | "customerPo"> | null;

function settledLine(r: Settled, placed: Placed): HTMLElement {
  if (r.status !== "APPLIED") return h("p", { class: "error" }, `Not done: ${plain(r.reason) || r.status.toLowerCase()}.`);
  const res = r.result;
  const orderId = placed?.customerPo?.customerPoId ?? res?.customerPoId;
  const where = placed?.vendorPo
    ? [" Recorded on vendor PO ", h("a", { class: "num", href: `/vendor-po/${placed.vendorPo.number}` }, placed.vendorPo.number), "."]
    : orderId ? [" ", h("a", { href: `/order/${orderId}` }, "Open the order")] : null;
  return h("p", { class: "ok" },
    r.action === "IGNORE" ? "Ignored: nothing more will be done with it."
      : r.action === "REREAD" ? "Being read again. In a minute, show the new reading: if it reads cleanly it leaves Home's list."
      : `Confirmed: the reading is right.${res?.note ? ` ${plain(res.note)}` : ""}`,
    r.action === "CONFIRM" ? where : null);
}

/** Reloads this screen, for the new reading after "Read it again". */
function reloadButton(): HTMLButtonElement {
  const b = h("button", { type: "button", class: "button" }, "Show the new reading");
  b.addEventListener("click", () => reload());
  return b;
}

function settlePanel(api: Api, documentId: number, documentNumber: string | null | undefined): HTMLElement {
  const result = h("div", { class: "result", "aria-live": "polite" });
  const po = h("input", { id: "po", type: "text", value: documentNumber ?? "", maxlength: 60, autocomplete: "off" });
  const reason = h("input", { id: "ignore-reason", type: "text", placeholder: "Why it needs nothing", maxlength: 500 });
  const reread = h("button", { type: "button", class: "button" }, "Read it again");
  const confirm = confirmButton("Confirm the reading", "Press again to confirm", () => {
    const n = po.value.trim();
    void act({ action: "CONFIRM", ...(n && n !== (documentNumber ?? "") ? { po_number: n } : {}) });
  }, "button-primary");
  const ignore = confirmButton("Ignore", "Press again to ignore it", () => void act({ action: "IGNORE", note: reason.value.trim() }),
    "button-quiet", () => {
      if (reason.value.trim()) return true;
      replace(result, h("p", { class: "error" }, "Say why it needs nothing, then ignore it."));
      return false;
    });
  const buttons = [confirm, reread, ignore];
  const act = async (args: Record<string, unknown>) => {
    buttons.forEach((b) => (b.disabled = true));
    try {
      const r = await api.tool<Settled>("settle_document", { document_id: documentId, ...args });
      // Confirmed: where it now sits (the vendor PO it's recorded on, or the order), from its reading.
      const placed: Placed = r.status === "APPLIED" && r.action === "CONFIRM"
        ? (await api.tool<Reading>("inbox", { document_id: documentId }).catch(() => ({} as Reading))).document ?? null
        : null;
      replace(result, settledLine(r, placed), r.status === "APPLIED" && r.action === "REREAD" ? reloadButton() : null);
      if (r.status === "APPLIED") {
        controls.remove();
        replace(statusSlot, pill(r.action === "IGNORE" ? "ignored" : r.action === "REREAD" ? "being read again" : "confirmed",
          r.action === "IGNORE" ? "neutral" : "good"));
      }
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
    } finally {
      buttons.forEach((b) => (b.disabled = false));
    }
  };
  reread.addEventListener("click", () => void act({ action: "REREAD" }));
  const statusSlot = h("div");
  const controls = h("div", { class: "actions" },
    h("div", { class: "field" }, h("label", { for: "po" }, "PO number, as it should read"), po),
    confirm,
    h("p", { class: "caption" }, "Confirming takes the reading as right (with the PO number above if you changed it) and " +
      "carries on as for a clean reading: the order is recorded and matched, and vendor POs drafted where they apply."),
    reread,
    h("div", { class: "field" }, h("label", { for: "ignore-reason" }, "Ignore, with a reason"), reason, ignore));
  return h("div", { class: "panel decision" }, statusSlot, controls, result);
}

/**
 * An unread document (kept unread: a sender Tare doesn't know past the day's limit, or ignored by someone) can still be
 * read: Read it (settle_document REREAD, documents.write). One press: reading changes nothing until someone acts on it.
 */
function readPanel(api: Api, documentId: number): HTMLElement {
  const result = h("div", { class: "result", "aria-live": "polite" });
  const b = h("button", { type: "button", class: "button" }, "Read it");
  b.addEventListener("click", async () => {
    b.disabled = true;
    try {
      const r = await api.tool<Settled>("settle_document", { document_id: documentId, action: "REREAD" });
      replace(result, settledLine(r, null), r.status === "APPLIED" ? reloadButton() : null);
      if (r.status === "APPLIED") b.remove();
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
    }
    b.disabled = false;
  });
  return h("div", { class: "panel decision" }, h("p", { class: "caption" }, "Not read. Read it to see what it is; nothing else happens until you act on it."), b, result);
}

export async function documentView(api: Api, me: Me, documentId: number): Promise<HTMLElement> {
  const r = await api.tool<Reading>("inbox", { document_id: documentId });
  const d = r.document;
  if (!d) return h("div", { class: "page" }, h("p", { class: "lede" }, r.error ?? "There's no such document."));
  const x = d.extracted;
  const po = x?.purchase_order;
  const fact = (name: string, value: string | null | undefined) => (value ? [label(name), h("span", null, value)] : null);

  return h("div", { class: "page" },
    d.customerPo
      ? crumbs(...orderCrumbs(d.customerPo.customerPoId, d.customerPo.poNumber), { text: d.filename, num: true })
      : crumbs({ text: "Home", href: "/" }, { text: "Documents to review" }, { text: d.filename, num: true }),
    h("div", { class: "stack-tight" },
      label(`${d.kind.toLowerCase().replace(/_/g, " ")} · ${d.status.toLowerCase().replace(/_/g, " ")}`),
      h("h1", { class: "title" }, x?.document_number ? `${d.filename}: ${x.document_number}` : d.filename),
      h("p", { class: "lede" }, x?.summary ?? `${d.subject}, from ${d.from}.`)),
    d.note ? h("div", { class: "notice" }, plain(d.note)) : null,
    h("div", { class: "layout" },
      h("div", { class: "main" },
        x?.uncertain?.length
          ? h("section", { class: "stack" }, h("h2", { class: "heading" }, "What the reader wasn't sure of"),
              h("ul", { class: "cards" }, x.uncertain.map((u) => h("li", { class: "card" }, h("span", { class: "num" }, u.field), h("span", null, u.reason)))))
          : null,
        po?.lines?.length
          ? h("section", { class: "stack" }, h("h2", { class: "heading" }, "Lines as read"),
              h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
                h("thead", null, h("tr", null, ["Line", "Part", "Description", "Qty", "Unit price", "Extended"].map((c, i) =>
                  h("th", { scope: "col", class: i >= 3 ? "r" : null }, c)))),
                h("tbody", null, po.lines.map((l) => h("tr", null,
                  h("td", { class: "num" }, l.line_no ?? "", l.line_type !== "ITEM" ? h("div", null, pill(l.line_type.toLowerCase().replace(/_/g, " "))) : null),
                  h("td", { class: "num" }, l.customer_part ?? ""),
                  h("td", null, l.description ?? ""),
                  h("td", { class: "num r" }, l.qty === null ? "" : `${l.qty.toLocaleString("en-US")} ${l.uom ?? ""}`),
                  h("td", { class: "num r" }, l.unit_price ? `${l.unit_price}${l.price_per ? ` / ${l.price_per}` : ""}` : ""),
                  h("td", { class: "num r" }, l.extended ?? "")))))))
          : null),
      h("aside", { class: "side" },
        ["NEEDS_REVIEW", "FAILED"].includes(d.status) && can(me, "documents.write") ? settlePanel(api, d.documentId, x?.document_number) : null,
        d.status === "IGNORED" && can(me, "documents.write") ? readPanel(api, d.documentId) : null,
        h("div", { class: "panel facts" },
          fact("From", `${d.from} to ${d.mailbox}@${d.knownSender === false ? " (a sender Tare doesn't know)" : ""}`), fact("Received", when(d.receivedAt)), fact("Subject", d.subject),
          fact("Buyer", po?.buyer_company), fact("Addressed to", po?.seller_company), fact("PO date", po?.po_date),
          fact("Requested", po?.requested_date), fact("Ship to", po?.ship_to), fact("Terms", po?.terms), fact("Total", po?.total),
          d.customerPo ? [label("Became"), h("a", { href: `/order/${d.customerPo.customerPoId}` }, `Customer PO ${d.customerPo.poNumber}`)] : null,
          d.vendorPo ? [label("Recorded on"), h("a", { class: "num", href: `/vendor-po/${d.vendorPo.number}` }, `Vendor PO ${d.vendorPo.number}`)] : null,
          fact("Read by", d.model ? `${d.model}${d.confidence !== null ? `, confidence ${Math.round(d.confidence * 100)}%` : ""}` : null)),
        ["NEEDS_REVIEW", "FAILED"].includes(d.status) && !can(me, "documents.write")
          ? h("p", { class: "caption" }, "Settling a document needs the documents.write permission.") : null)));
}
